<template>
  <!-- Conditional Rendering for Standard Footer -->
  <div
    v-if="footerType === 'standart-footer'"
    :style="{
      height: isMobile ? 'auto' : footerHeight,
      backgroundColor: footerColor,
    }"
    :class="[
      'footer',
      { 'footer-expanded-mobile': footerExpanded && windowWidth < 760 },
      { 'footer-mobile': windowWidth < 760 },
    ]"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    ref="footerRef"
  >
    <!-- ============ MOBILE FOOTER ============ -->
    <template v-if="isMobile">
      <div class="mobile-footer-wrapper">
        <!-- Background decoration -->
        <div class="footer-icon mobile-footer-bg">
          <img src="/images/footer-background.svg" alt="" aria-hidden="true" />
        </div>

        <!-- Top section: Logo + Address -->
        <div class="mobile-top-section">
          <div class="mobile-logo">
            <img
              height="35"
              src="/images/eddy.svg"
              alt="Eddy Logo"
              aria-hidden="true"
            />
          </div>
          <div class="mobile-address">
            <img
              class="mobile-location-icon"
              src="/images/location.svg"
              alt=""
              aria-hidden="true"
              height="24"
              width="24"
            />
            <div class="mobile-address-text">
              <span>edm8ker Inc.</span>
              <span>601 S Rancho Dr Ste C-25</span>
              <span>Las Vegas, NV 89106</span>
            </div>
          </div>
        </div>

        <!-- Accordion sections: driven by footerMenuSections -->
        <div
          v-for="section in footerMenuSections"
          :key="section.key"
          class="mobile-accordion-section"
        >
          <button
            class="mobile-accordion-header"
            @click="toggleAccordion(section.key)"
          >
            <span class="mobile-accordion-title">{{ section.title }}</span>
            <svg
              class="mobile-accordion-chevron"
              :class="{ 'chevron-open': accordionState[section.key] }"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="#003D6A"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div
            class="mobile-accordion-content"
            :class="{ 'accordion-open': accordionState[section.key] }"
          >
            <div class="mobile-accordion-body">
              <template v-for="item in section.items" :key="item.label">
                <!-- Contact row with icon -->
                <div v-if="item.icon" class="mobile-contact-row">
                  <img
                    :src="item.icon"
                    :alt="item.label"
                    width="20"
                    height="20"
                  />
                  <a
                    v-if="item.action"
                    @click="openModal(item.action)"
                    class="mobile-footer-link"
                    >{{ item.label }}</a
                  >
                  <router-link
                    v-else-if="item.to"
                    :to="item.to"
                    class="mobile-footer-link"
                    >{{ item.label }}</router-link
                  >
                  <a
                    v-else
                    :href="item.href"
                    :target="item.external ? '_blank' : undefined"
                    class="mobile-footer-link"
                    >{{ item.label }}</a
                  >
                  <svg
                    v-if="item.external"
                    class="mobile-external-link-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 17L17 7M17 7H7M17 7V17"
                      stroke="#003D6A"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <!-- Regular link -->
                <a
                  v-else-if="item.action"
                  @click="openModal(item.action)"
                  class="mobile-footer-link"
                  >{{ item.label }}</a
                >
                <router-link
                  v-else-if="item.to"
                  :to="item.to"
                  class="mobile-footer-link"
                  >{{ item.label }}</router-link
                >
                <a
                  v-else
                  :href="item.href"
                  :target="item.external ? '_blank' : undefined"
                  class="mobile-footer-link"
                  >{{ item.label }}</a
                >
              </template>
            </div>
          </div>
        </div>

        <!-- (Get In Touch accordion now rendered by the v-for above) -->

        <!-- Copyright -->
        <div class="mobile-copyright">
          ©{{ new Date().getFullYear() }} edm8ker. All rights reserved.
        </div>
      </div>
    </template>

    <!-- ============ DESKTOP FOOTER (unchanged) ============ -->
    <template v-else>
      <div class="footer-icon">
        <img src="/images/footer-background.svg" alt="" aria-hidden="true" />
      </div>
      <!-- Logo Section -->
      <template v-if="!footerExpanded">
        <div class="footer-col">
          <div class="logo-container">
            <img
              height="40"
              src="/images/eddy.svg"
              alt="Eddy Logo"
              aria-hidden="true"
            />
          </div>
        </div>

        <!-- Toggle Section -->
        <div class="footer-col mobile-arrow-icon">
          <img
            height="30"
            src="/images/arrow-square-right.svg"
            alt="Toggle footer"
            class="clickable-image rotate-icon"
            :class="{ 'expanded-rotate': footerExpanded }"
            @click="handleToggleClick"
          />
        </div>

        <!-- End Section -->
        <div class="footer-col text-end footer-background">
          <div class="edm8ker-copyright">
            ©{{ new Date().getFullYear() }} edm8ker. All rights reserved.
          </div>
        </div>
      </template>

      <!-- Expanded Content -->
      <template v-if="footerExpanded">
        <div
          class="footer-expanded-content footer-content-top-padding"
          style="
            display: flex;
            align-items: start;
            flex-direction: column;
            height: 100%;
          "
        >
          <div class="logo-container">
            <img
              height="40"
              src="/images/eddy.svg"
              alt="Eddy Logo"
              aria-hidden="true"
            />
          </div>
          <!-- Address Section -->
          <div class="footer-address-section flex-container">
            <img
              class="location-icon"
              height="30"
              src="/images/location.svg"
              alt=""
              aria-hidden="true"
            />
            <div>
              <div class="footer-label">edm8ker Inc.</div>
              <div class="footer-label">601 S Rancho Dr Ste C-25</div>
              <div class="footer-label">Las Vegas, NV 89106</div>
            </div>
          </div>
        </div>

        <!-- Information Sections: driven by footerMenuSections -->
        <div
          class="footer-expanded-content"
          style="
            width: 60%;
            height: 100%;
            display: flex;
            justify-content: space-around;
          "
        >
          <div
            v-for="(section, index) in footerMenuSections"
            :key="section.key"
            class="footer-column-top-padding"
            :class="
              index === footerMenuSections.length - 1
                ? 'column-right'
                : 'column'
            "
          >
            <div>
              <div class="footer-label-title">{{ section.title }}</div>
              <div class="get-in-touch">
                <div
                  v-for="item in section.items"
                  :key="item.label"
                  class="footer-label"
                  :class="item.icon ? 'footer-link-row' : ''"
                >
                  <img
                    v-if="item.icon"
                    :src="item.icon"
                    width="20"
                    height="20"
                    style="flex-shrink: 0; margin-right: 6px"
                  />
                  <a
                    v-if="item.action"
                    @click="openModal(item.action)"
                    class="footer-link"
                    >{{ item.label }}</a
                  >
                  <router-link
                    v-else-if="item.to"
                    :to="item.to"
                    class="footer-link"
                    >{{ item.label }}</router-link
                  >
                  <a
                    v-else
                    :href="item.href"
                    :target="item.external ? '_blank' : undefined"
                    class="footer-link"
                    :class="item.icon ? 'footer-link-no-margin' : ''"
                    >{{ item.label }}</a
                  >
                  <!-- External link arrow icon (same as mobile) -->
                  <svg
                    v-if="item.icon && item.external"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style="flex-shrink: 0; margin-left: 4px"
                  >
                    <path
                      d="M7 17L17 7M17 7H7M17 7V17"
                      stroke="#003D6A"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer End Section (Expanded) -->
        <div
          class="footer-expanded-content footer-content-bottom-padding"
          style="
            display: flex;
            justify-content: flex-end;
            flex-direction: column;
            height: 100%;
          "
        >
          <div class="edm8ker-copyright">
            ©{{ new Date().getFullYear() }} edm8ker. All rights reserved.
          </div>
        </div>
      </template>
    </template>

    <NotificationSettingModal
      :isOpen="modals.notification"
      @close="closeModal('notification')"
    />
  </div>

  <!-- Conditional Rendering for Simplified Footer -->
  <div v-else-if="footerType === 'simplified-footer'" class="simplified-footer">
    ©{{ new Date().getFullYear() }} edm8ker
  </div>
</template>

<script setup>
/* eslint-disable */
import {
  computed,
  defineProps,
  onMounted,
  onUnmounted,
  reactive,
  ref,
} from "vue";
import NotificationSettingModal from "./modal/NotificationSettingModal.vue";

defineProps({
  footerType: {
    type: String,
    default: "standart-footer",
  },
});

const footerExpanded = ref(false);
const footerHeight = ref("88px");
const footerColor = ref("#EAECFF");
const footerRef = ref(null);
const windowWidth = ref(
  typeof window !== "undefined" ? window.innerWidth : 760
);

const isMobile = computed(() => windowWidth.value < 760);

const footerMenuSections = [
  {
    key: "about",
    title: "About Us",
    items: [
      { label: "edm8ker", href: "http://www.edm8ker.com" },
      {
        label: "Sustainable Living Lab",
        href: "https://www.sustainablelivinglab.org/",
      },
    ],
  },
  {
    key: "helpLegal",
    title: "Help/Legal",
    items: [
      { label: "Privacy Policies", to: "/privacy-policy" },
      { label: "Terms of Service", to: "/terms-of-service" },
      { label: "Notification Settings", action: "notification" },
    ],
  },
  {
    key: "getInTouch",
    title: "Get In Touch",
    items: [
      {
        label: "hello@eddy4teachers.com",
        href: "mailto:hello@eddy4teachers.com",
        external: true,
        icon: "/images/email.svg",
      },
      {
        label: "LinkedIn",
        href: "https://www.linkedin.com/showcase/eddy4teachers/",
        external: true,
        icon: "/images/social-icon.svg",
      },
    ],
  },
];
// ──────────────────────────────────────────────────────────────────────────────

// Accordion state for mobile - all closed by default
// Keys are derived from footerMenuSections so they stay in sync automatically.
const accordionState = reactive(
  Object.fromEntries(footerMenuSections.map((s) => [s.key, false]))
);

const toggleAccordion = (section) => {
  accordionState[section] = !accordionState[section];
};

const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  if (typeof window !== "undefined") {
    window.addEventListener("resize", updateWindowWidth);
  }
});

onUnmounted(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", updateWindowWidth);
  }
});

const modals = reactive({
  notification: false,
});

// Compute if any modal is open
const isModalOpen = computed(() => {
  return Object.values(modals).some((value) => value);
});

// Track mouse over state
const isMouseOverFooter = ref(false);

const toggleFooter = (expand) => {
  const isMobileView = window.innerWidth < 760;

  // If a modal is open, don't collapse the footer
  if (!expand && isModalOpen.value) {
    return;
  }

  footerExpanded.value = expand;

  if (isMobileView) {
    footerHeight.value = footerExpanded.value ? "auto" : "88px";
  } else {
    footerHeight.value = footerExpanded.value ? "280px" : "88px";
  }

  // Auto-scroll to show full footer on desktop when expanded
  // Wait for the CSS height transition (0.2s) to complete before scrolling
  if (expand && !isMobileView && footerRef.value) {
    setTimeout(() => {
      if (footerRef.value) {
        // Calculate exact position in one scroll call to avoid animation conflicts
        const rect = footerRef.value.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetY = scrollTop + rect.bottom - window.innerHeight + 200;
        window.scrollTo({ top: targetY, behavior: "smooth" });
      }
    }, 350);
  }
};

const handleToggleClick = () => {
  toggleFooter(!footerExpanded.value);
};

// Unified method for opening any modal
const openModal = (modalType) => {
  if (!isMobile.value) {
    toggleFooter(true);
  }
  modals[modalType] = true;
};

// Unified method for closing any modal
const closeModal = (modalType) => {
  modals[modalType] = false;

  // Check if other modals are still open before considering collapsing the footer
  if (!isModalOpen.value && !isMouseOverFooter.value) {
    toggleFooter(false);
  }
};

const handleMouseEnter = () => {
  if (window.innerWidth >= 760) {
    isMouseOverFooter.value = true;
    toggleFooter(true);
  }
};

const handleMouseLeave = () => {
  if (window.innerWidth >= 760) {
    isMouseOverFooter.value = false;
    toggleFooter(false);
  }
};
</script>

<style scoped>
/* ============ DESKTOP STYLES (unchanged) ============ */
.footer {
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  transition: height 0.5s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  position: relative;
  overflow: visible;
}

.footer-col {
  flex: 1;
  text-align: center;
}

.clickable-image {
  cursor: pointer;
}

.rotate-icon {
  transition: transform 0.5s ease;
}

.expanded-rotate {
  transform: rotate(180deg);
}

.text-end {
  text-align: right;
}

.footer-icon {
  position: absolute;
  right: -20px;
  top: 0;
  bottom: 0;
}

.footer-icon img {
  height: 100%;
  object-fit: fit;
}

.footer-background::after {
  content: "";
  position: absolute;
  right: 0;
  bottom: 0;
  width: 414px;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: bottom right;
  z-index: 9999;
}

.footer-label-title {
  font-family: "League Spartan";
  font-size: var(--fs-big);
  font-weight: 600;
  color: var(--primary-blue);
  margin-bottom: 0.4em;
}

.footer-label-title::after {
  content: "";
  display: block;
  border-bottom: 1.5px solid var(--primary-blue);
  margin-top: 0.4em;
  width: 40%;
}

.location-icon {
  padding-right: 0.25rem;
  flex: 1;
  margin-right: 0 !important;
}

.flex-container {
  display: flex;
}

.flex-item {
  margin-right: 10px;
}

.footer-expanded-content {
  display: flex;
}

.column {
  display: flex;
  flex-direction: column;
  width: 18%;
}

.column-right {
  display: flex;
  flex-direction: column;
  width: 22%;
}

.get-in-touch {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.edm8ker-copyright {
  font-size: var(--fs-medium);
  position: relative;
  z-index: 1;
  font-weight: 400;
  color: var(--primary-blue);
  font-family: "League Spartan";
}

.simplified-footer {
  color: var(--primary-blue);
  font-family: "League Spartan";
  padding: 12px 0px 10px 0px;
  background-color: #eaecff;
  font-weight: 500;
  display: flex;
  justify-content: center;
}

.footer-link {
  cursor: pointer;
  text-decoration: none;
  margin-bottom: 10px !important;
  color: var(--primary-blue);
}

/* Row that contains an icon + link — overrides the margin-bottom on the <a>
   so that align-items: center actually centres them correctly. */
.footer-link-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.footer-link-no-margin {
  margin-bottom: 0 !important;
}

.footer-address-section {
  padding-left: 1rem;
  padding-top: 1rem;
}

.footer-content-top-padding {
  padding-top: 0.75rem;
}

.footer-content-bottom-padding {
  padding-bottom: 0.75rem;
}

.footer-column-top-padding {
  padding-top: 1rem;
}

.footer-image-right-padding {
  padding-right: 0.25rem;
}

.footer-col-mobile-toggle {
  display: none;
}

.edm8ker-copyright-mobile {
  display: none;
}

@media (min-width: 761px) {
  .footer-col-mobile-toggle {
    display: none !important;
  }
}

/* ============ MOBILE FOOTER STYLES ============ */
@media (max-width: 760px) {
  .footer.footer-mobile {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 0;
    height: auto !important;
    overflow: visible;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  }

  .mobile-footer-wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 40px 16px;
    width: 100%;
    position: relative;
    overflow: hidden;
  }

  .mobile-footer-bg {
    position: absolute;
    right: -20px;
    bottom: 0;
    top: auto;
    z-index: 0;
    pointer-events: none;
  }

  .mobile-footer-bg img {
    height: 328px;
    object-fit: contain;
  }

  /* Top section: Logo + Address */
  .mobile-top-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    position: relative;
    z-index: 1;
  }

  .mobile-logo {
    flex-shrink: 0;
  }

  .mobile-address {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-shrink: 0;
  }

  .mobile-location-icon {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .mobile-address-text {
    display: flex;
    flex-direction: column;
    font-family: "League Spartan", sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.04px;
    color: var(--primary-blue);
  }

  /* Accordion Section */
  .mobile-accordion-section {
    width: 100%;
    position: relative;
    z-index: 1;
  }

  .mobile-accordion-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 10px 16px;
    height: 56px;
    border: none;
    border-bottom: 1px solid var(--primary-blue);
    background: transparent;
    cursor: pointer;
    box-sizing: border-box;
  }

  .mobile-accordion-title {
    font-family: "League Spartan", sans-serif;
    font-weight: 700;
    font-size: var(--fs-big);
    line-height: 24px;
    letter-spacing: 0.015em;
    color: var(--primary-blue);
  }

  .mobile-accordion-chevron {
    flex-shrink: 0;
    transition: transform 0.3s ease;
    transform: rotate(180deg);
  }

  .mobile-accordion-chevron.chevron-open {
    transform: rotate(0deg);
  }

  .mobile-accordion-content {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
  }

  .mobile-accordion-content.accordion-open {
    max-height: 500px;
  }

  .mobile-accordion-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 24px;
  }

  .mobile-footer-link {
    font-family: "League Spartan", sans-serif;
    font-weight: 400;
    font-size: var(--fs-big);
    line-height: 24px;
    letter-spacing: 0.015em;
    color: var(--primary-blue);
    text-decoration: none;
    cursor: pointer;
    display: block;
  }

  /* Contact rows */
  .mobile-contact-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mobile-contact-row img {
    flex-shrink: 0;
  }

  .mobile-contact-row .mobile-footer-link {
    flex-shrink: 0;
  }

  .mobile-external-link-icon {
    flex-shrink: 0;
  }

  /* Copyright */
  .mobile-copyright {
    font-family: "League Spartan", sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.04px;
    color: var(--primary-blue);
    width: 100%;
    position: relative;
    z-index: 1;
  }
}
</style>
